import React, { useMemo, useCallback, useState } from "react";
import {
  Paper,
  Stack,
  Group,
  Text,
  Box,
  Center,
  Skeleton,
  Badge,
  Tooltip,
  UnstyledButton,
  Collapse,
} from "@mantine/core";
import { useTranslation } from "react-i18next";
import dayjs from "dayjs";

import {
  VehicleRecall,
  VEHICLE_RECALL_STATUS,
  getRecallStatusLabel,
  getRecallStatusColor,
  getRecallSourceLabel,
  isRecallSafetyCritical,
  getRecallEffectiveId,
  VehicleRecallStatusValue,
} from "@/utils/api";
import {
  AppTable,
  AppTableColumn,
  SortConfig,
  AppButton,
  AppIcon,
  EmptyState,
  SwipeableCard,
  SwipeAction,
  useSwipeableList,
} from "@/components/generic";

// ============================================
// Types
// ============================================

export interface VehicleRecallsListProps {
  /** Array of vehicle recall records to display */
  recalls: VehicleRecall[];

  /** Loading state for initial load */
  loading: boolean;

  /** Loading state for loading more items */
  loadingMore?: boolean;

  /** Whether there are more items to load */
  hasMore?: boolean;

  /** Callback to load more items */
  onLoadMore?: () => void;

  /** Current sort configuration */
  sortBy?: SortConfig[];

  /** Callback when sort changes */
  onSortChange?: (sortBy: SortConfig[]) => void;

  /** Callback when dismiss action is clicked */
  onDismissRecall?: (recall: VehicleRecall) => void;

  /** Callback when resolve action is clicked */
  onResolveRecall?: (recall: VehicleRecall) => void;

  /** Callback when reopen action is clicked */
  onReopenRecall?: (recall: VehicleRecall) => void;

  /** Callback when a recall row/card is clicked (view details) */
  onRecallClick?: (recall: VehicleRecall) => void;

  /** ID of recall currently being updated */
  updatingRecallId?: string | null;

  /** ID of recently updated recall to highlight */
  highlightedRecallId?: string | null;
}

// ============================================
// Status Badge Component
// ============================================

interface RecallStatusBadgeProps {
  status?: VehicleRecallStatusValue;
  size?: "xs" | "sm" | "md";
}

const RecallStatusBadge: React.FC<RecallStatusBadgeProps> = ({
  status,
  size = "md",
}) => {
  const { t } = useTranslation(["vehicleRecalls"]);

  if (status == null) return null;

  const labelMap: Record<number, string> = {
    [VEHICLE_RECALL_STATUS.ACTIVE]: t("vehicleRecalls:status.active"),
    [VEHICLE_RECALL_STATUS.DISMISSED]: t("vehicleRecalls:status.dismissed"),
    [VEHICLE_RECALL_STATUS.RESOLVED]: t("vehicleRecalls:status.resolved"),
  };

  return (
    <Badge color={getRecallStatusColor(status)} variant="light" size={size}>
      {labelMap[status] || getRecallStatusLabel(status)}
    </Badge>
  );
};

// ============================================
// Safety Flags Component
// ============================================

interface SafetyFlagsProps {
  recall: VehicleRecall;
  size?: "xs" | "sm";
}

const SafetyFlags: React.FC<SafetyFlagsProps> = ({ recall, size = "sm" }) => {
  const { t } = useTranslation(["vehicleRecalls"]);

  if (!recall.parkIt && !recall.parkOutside && !recall.otaUpdate) return null;

  return (
    <Group gap={4} wrap="wrap">
      {recall.parkIt && (
        <Tooltip label={t("vehicleRecalls:safety.doNotDrive")}>
          <Badge color="red" variant="filled" size={size} leftSection={
            <AppIcon iconName="AlertOctagon" size={12} />
          }>
            {t("vehicleRecalls:safety.doNotDriveShort")}
          </Badge>
        </Tooltip>
      )}
      {recall.parkOutside && (
        <Tooltip label={t("vehicleRecalls:safety.parkOutside")}>
          <Badge color="orange" variant="filled" size={size} leftSection={
            <AppIcon iconName="Flame" size={12} />
          }>
            {t("vehicleRecalls:safety.parkOutsideShort")}
          </Badge>
        </Tooltip>
      )}
      {recall.otaUpdate && (
        <Tooltip label={t("vehicleRecalls:safety.otaUpdate")}>
          <Badge color="blue" variant="light" size={size} leftSection={
            <AppIcon iconName="Wifi" size={12} />
          }>
            {t("vehicleRecalls:safety.otaUpdateShort")}
          </Badge>
        </Tooltip>
      )}
    </Group>
  );
};

// ============================================
// Expandable Details Component
// ============================================

interface ExpandableDetailsProps {
  consequence?: string | null;
  remedy?: string | null;
}

const ExpandableDetailsComponent: React.FC<ExpandableDetailsProps> = ({
  consequence,
  remedy,
}) => {
  const { t } = useTranslation(["vehicleRecalls", "common"]);
  const [expanded, setExpanded] = useState(false);

  const hasDetails = Boolean(consequence) || Boolean(remedy);
  if (!hasDetails) return null;

  const handleToggle = useCallback((e: React.MouseEvent) => {
    e.stopPropagation();
    setExpanded((prev) => !prev);
  }, []);

  return (
    <Stack gap={4}>
      <UnstyledButton onClick={handleToggle}>
        <Group gap={4} wrap="nowrap">
          <AppIcon
            iconName={expanded ? "ChevronUp" : "ChevronDown"}
            size={16}
            color="blue"
          />
          <Text size="md" c="blue" fw={500}>
            {expanded
              ? t("common:buttons.hideDetails")
              : t("common:buttons.showDetails")}
          </Text>
        </Group>
      </UnstyledButton>

      <Collapse in={expanded}>
        <Stack gap="sm" pt={4}>
          {consequence && (
            <Stack gap={2}>
              <Group gap={6} wrap="nowrap" align="center">
                <AppIcon
                  iconName="AlertTriangle"
                  size={16}
                  color="orange"
                  style={{ flexShrink: 0 }}
                />
                <Text size="md" fw={600} c="orange.8">
                  {t("vehicleRecalls:list.labels.consequence")}
                </Text>
              </Group>
              <Text size="md" c="dimmed" pl={22}>
                {consequence}
              </Text>
            </Stack>
          )}
          {remedy && (
            <Stack gap={2}>
              <Group gap={6} wrap="nowrap" align="center">
                <AppIcon
                  iconName="Tool"
                  size={16}
                  color="gray"
                  style={{ flexShrink: 0 }}
                />
                <Text size="md" fw={600}>
                  {t("vehicleRecalls:list.labels.remedy")}
                </Text>
              </Group>
              <Text size="md" c="dimmed" pl={22}>
                {remedy}
              </Text>
            </Stack>
          )}
        </Stack>
      </Collapse>
    </Stack>
  );
};

const ExpandableDetails = React.memo(ExpandableDetailsComponent);

// ============================================
// Loading Skeletons
// ============================================

/** Mobile card skeleton */
const RecallCardSkeleton: React.FC = () => {
  return (
    <Paper p="md" withBorder>
      <Stack gap="md">
        {/* Header: Component + Status */}
        <Group justify="space-between" align="flex-start">
          <Stack gap={4} style={{ flex: 1 }}>
            <Skeleton height={20} width={200} />
            <Skeleton height={16} width={120} />
          </Stack>
          <Skeleton height={22} width={70} radius="sm" />
        </Group>

        {/* Campaign + Source */}
        <Group gap="md">
          <Skeleton height={16} width={140} />
          <Skeleton height={16} width={80} />
        </Group>

        {/* Summary */}
        <Stack gap={4}>
          <Skeleton height={16} width="90%" />
          <Skeleton height={16} width="70%" />
        </Stack>

        {/* Car */}
        <Group gap={6}>
          <Skeleton height={16} width={16} radius="sm" />
          <Skeleton height={16} width={100} />
        </Group>
      </Stack>
    </Paper>
  );
};

/** Mobile cards skeleton */
const CardsSkeleton: React.FC<{ count?: number }> = ({ count = 3 }) => {
  return (
    <Stack gap="md">
      {Array.from({ length: count }).map((_, index) => (
        <RecallCardSkeleton key={index} />
      ))}
    </Stack>
  );
};

/** Desktop table skeleton */
const TableSkeleton: React.FC<{ count?: number }> = ({ count = 5 }) => {
  return (
    <Paper withBorder radius="sm">
      {/* Table header */}
      <Group
        px="md"
        py="sm"
        gap="md"
        wrap="nowrap"
        style={{ borderBottom: "1px solid var(--mantine-color-gray-2)" }}
        bg="gray.0"
      >
        <Box w={120}>
          <Skeleton height={16} width={90} />
        </Box>
        <Box style={{ flex: 1, minWidth: 300 }}>
          <Skeleton height={16} width={160} />
        </Box>
        <Box w={130}>
          <Skeleton height={16} width={80} />
        </Box>
        <Box w={110}>
          <Skeleton height={16} width={70} />
        </Box>
        <Box w={110} />
      </Group>

      {/* Table rows */}
      {Array.from({ length: count }).map((_, index) => (
        <Group
          key={index}
          px="md"
          py="sm"
          gap="md"
          wrap="nowrap"
          align="flex-start"
          style={{
            borderBottom:
              index < count - 1
                ? "1px solid var(--mantine-color-gray-2)"
                : undefined,
          }}
        >
          <Box w={120}>
            <Skeleton height={16} width={100} />
          </Box>
          <Box style={{ flex: 1, minWidth: 300 }}>
            <Stack gap={8}>
              <Skeleton height={18} width="40%" />
              <Skeleton height={16} width="90%" />
              <Skeleton height={16} width="70%" />
            </Stack>
          </Box>
          <Box w={130}>
            <Skeleton height={16} width={90} />
          </Box>
          <Box w={110}>
            <Skeleton height={22} width={70} radius="xl" />
          </Box>
          <Box w={110}>
            <Group gap={4} justify="flex-end">
              <Skeleton height={28} width={28} radius="sm" />
              <Skeleton height={28} width={28} radius="sm" />
            </Group>
          </Box>
        </Group>
      ))}
    </Paper>
  );
};

/** Responsive loading skeleton */
const LoadingSkeleton: React.FC = () => {
  return (
    <>
      <Box hiddenFrom="md">
        <CardsSkeleton count={3} />
      </Box>
      <Box visibleFrom="md">
        <TableSkeleton count={5} />
      </Box>
    </>
  );
};

// ============================================
// Recall Card Component (Mobile)
// ============================================

interface RecallCardProps {
  recall: VehicleRecall;
  onClick?: (recall: VehicleRecall) => void;
  onDismiss?: (recall: VehicleRecall) => void;
  onResolve?: (recall: VehicleRecall) => void;
  onReopen?: (recall: VehicleRecall) => void;
  highlighted: boolean;
  isUpdating: boolean;
  // Swipe management props
  forceClose?: boolean;
  onForceCloseComplete?: () => void;
  onSwipeStateChange?: (isOpen: boolean) => void;
}

const RecallCardComponent: React.FC<RecallCardProps> = ({
  recall,
  onClick,
  onDismiss,
  onResolve,
  onReopen,
  highlighted,
  isUpdating,
  forceClose,
  onForceCloseComplete,
  onSwipeStateChange,
}) => {
  const { t } = useTranslation(["vehicleRecalls", "common"]);

  const isActive = recall.status === VEHICLE_RECALL_STATUS.ACTIVE;
  const isDismissed = recall.status === VEHICLE_RECALL_STATUS.DISMISSED;
  const isResolved = recall.status === VEHICLE_RECALL_STATUS.RESOLVED;
  const isSafetyCritical = isRecallSafetyCritical(recall);

  // Format report received date
  const reportDateDisplay = useMemo(() => {
    if (!recall.reportReceivedDate) return null;
    return dayjs(recall.reportReceivedDate).format("MMM DD, YYYY");
  }, [recall.reportReceivedDate]);

  // Build swipe actions
  const leftSwipeActions = useMemo((): SwipeAction[] => {
    const actions: SwipeAction[] = [];

    // Resolve action (for active or dismissed recalls)
    if (!isResolved && onResolve) {
      actions.push({
        key: "resolve",
        icon: "CircleCheck",
        label: t("vehicleRecalls:actions.resolve"),
        color: "green",
        onClick: () => onResolve(recall),
        disabled: isUpdating,
      });
    }

    // Dismiss action (for active recalls)
    if (isActive && onDismiss) {
      actions.push({
        key: "dismiss",
        icon: "EyeOff",
        label: t("vehicleRecalls:actions.dismiss"),
        color: "gray",
        onClick: () => onDismiss(recall),
        disabled: isUpdating,
      });
    }

    // Reopen action (for dismissed or resolved recalls)
    if ((isDismissed || isResolved) && onReopen) {
      actions.push({
        key: "reopen",
        icon: "RotateClockwise",
        label: t("vehicleRecalls:actions.reopen"),
        color: "blue",
        onClick: () => onReopen(recall),
        disabled: isUpdating,
      });
    }

    return actions;
  }, [
    isActive,
    isDismissed,
    isResolved,
    isUpdating,
    onDismiss,
    onResolve,
    onReopen,
    recall,
    t,
  ]);

  // Handle card click
  const handleCardClick = useCallback(() => {
    if (!isUpdating && onClick) {
      onClick(recall);
    }
  }, [isUpdating, onClick, recall]);

  // Handle swipe state change
  const handleSwipeStateChange = useCallback(
    (isOpen: boolean) => {
      onSwipeStateChange?.(isOpen);
    },
    [onSwipeStateChange]
  );

  // Card content
  const cardContent = (
    <Paper
      p="md"
      withBorder
      bg={highlighted ? "green.0" : isSafetyCritical && isActive ? "red.0" : undefined}
      opacity={isUpdating ? 0.5 : 1}
      style={{
        transition: "opacity 150ms ease",
        cursor: onClick ? "pointer" : undefined,
      }}
      onClick={handleCardClick}
    >
      <Stack gap="sm">
        {/* Header: Component + Status */}
        <Group justify="space-between" align="flex-start" wrap="nowrap">
          <Stack gap={2} style={{ flex: 1, minWidth: 0 }}>
            <Text size="md" fw={600}>
              {recall.component || t("vehicleRecalls:list.unknownComponent")}
            </Text>
            {recall.manufacturer && (
              <Text size="md" c="dimmed" lineClamp={1}>
                {recall.manufacturer}
              </Text>
            )}
          </Stack>
          <RecallStatusBadge status={recall.status} size="md" />
        </Group>

        {/* Safety flags */}
        <SafetyFlags recall={recall} size="sm" />

        {/* Summary */}
        {recall.summary && (
          <Text size="md" c="dimmed">
            {recall.summary}
          </Text>
        )}

        {/* Expandable Consequence & Remedy */}
        <ExpandableDetails
          consequence={recall.consequence}
          remedy={recall.remedy}
        />

        {/* Campaign number + Source */}
        <Group gap="md" wrap="wrap">
          {recall.campaignNumber && (
            <Group gap={6} wrap="nowrap">
              <AppIcon iconName="Hash" size={16} color="gray" style={{ flexShrink: 0 }} />
              <Text size="md" c="dimmed">
                {recall.campaignNumber}
              </Text>
            </Group>
          )}
          {recall.source && (
            <Group gap={6} wrap="nowrap">
              <AppIcon iconName="Building" size={16} color="gray" style={{ flexShrink: 0 }} />
              <Text size="md" c="dimmed">
                {getRecallSourceLabel(recall.source)}
              </Text>
            </Group>
          )}
        </Group>

        {/* Report date */}
        {reportDateDisplay && (
          <Group gap={6} wrap="nowrap">
            <AppIcon iconName="Calendar" size={16} color="gray" style={{ flexShrink: 0 }} />
            <Text size="md" c="dimmed">
              {reportDateDisplay}
            </Text>
          </Group>
        )}

        {/* Car */}
        {recall.car && (
          <Group gap={6} wrap="nowrap">
            <AppIcon iconName="Car" size={16} color="gray" style={{ flexShrink: 0 }} />
            <Text size="md" c="dimmed" lineClamp={1}>
              {recall.car.label}
            </Text>
          </Group>
        )}

        {/* User notes */}
        {recall.userNotes && (
          <Group gap={6} wrap="nowrap" align="flex-start">
            <AppIcon iconName="Note" size={16} color="gray" style={{ flexShrink: 0, marginTop: 2 }} />
            <Text size="md" c="dimmed" lineClamp={2} fs="italic">
              {recall.userNotes}
            </Text>
          </Group>
        )}
      </Stack>
    </Paper>
  );

  // Wrap in SwipeableCard if there are actions
  if (leftSwipeActions.length > 0) {
    return (
      <SwipeableCard
        leftSwipeActions={leftSwipeActions}
        forceClose={forceClose}
        onForceCloseComplete={onForceCloseComplete}
        onSwipeStateChange={handleSwipeStateChange}
        disabled={isUpdating}
        borderRadius="var(--mantine-radius-default)"
      >
        {cardContent}
      </SwipeableCard>
    );
  }

  return cardContent;
};

const RecallCard = React.memo(RecallCardComponent);

// ============================================
// Main Component
// ============================================

const VehicleRecallsListComponent: React.FC<VehicleRecallsListProps> = ({
  recalls,
  loading,
  loadingMore = false,
  hasMore = false,
  onLoadMore,
  sortBy = [],
  onSortChange,
  onDismissRecall,
  onResolveRecall,
  onReopenRecall,
  onRecallClick,
  updatingRecallId,
  highlightedRecallId,
}) => {
  const { t } = useTranslation(["vehicleRecalls", "common"]);

  // Manage swipeable cards - ensures only one is open at a time
  const { openCard, shouldForceClose, handleForceCloseComplete } =
    useSwipeableList();

  // Handle swipe state change for a card
  const handleSwipeStateChange = useCallback(
    (recallId: string, isOpen: boolean) => {
      if (isOpen) {
        openCard(recallId);
      }
    },
    [openCard]
  );

  // Table columns for desktop
  const columns = useMemo((): AppTableColumn<VehicleRecall>[] => {
    return [
      {
        key: "reportReceivedDate",
        label: t("vehicleRecalls:list.columns.date"),
        sortable: true,
        width: 120,
        minWidth: 110,
        verticalAlign: "top",
        render: (value) => {
          if (!value) {
            return (
              <Text size="md" c="dimmed">
                —
              </Text>
            );
          }
          return <Text size="md">{dayjs(value).format("MMM DD, YYYY")}</Text>;
        },
      },
      {
        key: "component",
        label: t("vehicleRecalls:list.columns.recall"),
        sortable: false,
        flex: true,
        minWidth: 300,
        verticalAlign: "top",
        render: (value, record) => {
          return (
            <Stack gap="xs">
              {/* Component name as header + car + safety flags */}
              <Group gap="sm" justify="space-between" align="flex-start" wrap="nowrap">
                <Stack gap={2} style={{ flex: 1, minWidth: 0 }}>
                  <Text size="md" fw={700}>
                    {value || t("vehicleRecalls:list.unknownComponent")}
                  </Text>
                  {record.car && (
                    <Text size="md" c="dimmed">
                      {record.car.label}
                    </Text>
                  )}
                </Stack>
                <SafetyFlags recall={record} size="sm" />
              </Group>

              {/* Summary text */}
              {record.summary ? (
                <Text size="md">{record.summary}</Text>
              ) : (
                <Text size="md" c="dimmed">—</Text>
              )}

              {/* Expandable consequence & remedy */}
              <ExpandableDetails
                consequence={record.consequence}
                remedy={record.remedy}
              />
            </Stack>
          );
        },
      },
      {
        key: "campaignNumber",
        label: t("vehicleRecalls:list.columns.campaign"),
        sortable: false,
        width: 130,
        minWidth: 110,
        verticalAlign: "top",
        render: (value, record) => {
          return (
            <Stack gap={2}>
              {value && (
                <Text size="md" ff="monospace">
                  {value}
                </Text>
              )}
              {record.source && (
                <Text size="md" c="dimmed">
                  {getRecallSourceLabel(record.source)}
                </Text>
              )}
            </Stack>
          );
        },
      },
      {
        key: "status",
        label: t("vehicleRecalls:list.columns.status"),
        sortable: true,
        width: 110,
        minWidth: 100,
        verticalAlign: "top",
        render: (value) => <RecallStatusBadge status={value} size="md" />,
      },
      {
        key: "actions",
        label: "",
        width: 110,
        minWidth: 110,
        align: "right",
        verticalAlign: "top",
        render: (_, record) => {
          const isActive = record.status === VEHICLE_RECALL_STATUS.ACTIVE;
          const isDismissed = record.status === VEHICLE_RECALL_STATUS.DISMISSED;
          const isResolved = record.status === VEHICLE_RECALL_STATUS.RESOLVED;
          const isRecordUpdating = updatingRecallId != null && updatingRecallId === getRecallEffectiveId(record);

          return (
            <Group
              gap="xs"
              justify="flex-end"
              wrap="nowrap"
              onClick={(e: React.MouseEvent) => e.stopPropagation()}
            >
              {/* Resolve button (for active or dismissed) */}
              {!isResolved && onResolveRecall && (
                <AppButton
                  variant="secondary"
                  size="xs"
                  color="green"
                  onClick={() => onResolveRecall(record)}
                  tooltip={t("vehicleRecalls:actions.resolve")}
                  inProgress={isRecordUpdating}
                  disabled={updatingRecallId != null}
                >
                  <AppIcon iconName="CircleCheck" size={16} />
                </AppButton>
              )}

              {/* Dismiss button (for active) */}
              {isActive && onDismissRecall && (
                <AppButton
                  variant="secondary"
                  size="xs"
                  color="gray"
                  onClick={() => onDismissRecall(record)}
                  tooltip={t("vehicleRecalls:actions.dismiss")}
                  inProgress={isRecordUpdating}
                  disabled={updatingRecallId != null}
                >
                  <AppIcon iconName="EyeOff" size={16} />
                </AppButton>
              )}

              {/* Reopen button (for dismissed or resolved) */}
              {(isDismissed || isResolved) && onReopenRecall && (
                <AppButton
                  variant="secondary"
                  size="xs"
                  color="blue"
                  onClick={() => onReopenRecall(record)}
                  tooltip={t("vehicleRecalls:actions.reopen")}
                  inProgress={isRecordUpdating}
                  disabled={updatingRecallId != null}
                >
                  <AppIcon iconName="RotateClockwise" size={16} />
                </AppButton>
              )}
            </Group>
          );
        },
      },
    ];
  }, [
    t,
    onDismissRecall,
    onResolveRecall,
    onReopenRecall,
    updatingRecallId,
  ]);

  // Render load more button
  const renderLoadMore = useCallback(() => {
    if (!hasMore || loading || !onLoadMore) return null;

    return (
      <Center p="md">
        <AppButton
          variant="secondary"
          onClick={onLoadMore}
          inProgress={loadingMore}
        >
          {t("common:buttons.loadMore")}
        </AppButton>
      </Center>
    );
  }, [hasMore, loading, loadingMore, onLoadMore, t]);

  // Loading state
  if (loading && recalls.length === 0) {
    return <LoadingSkeleton />;
  }

  // Empty state
  if (!loading && recalls.length === 0) {
    return (
      <Paper withBorder p="xl">
        <EmptyState
          title={t("vehicleRecalls:emptyState.title")}
          description={t("vehicleRecalls:emptyState.description")}
          iconName="ShieldCheck"
          size="md"
        />
      </Paper>
    );
  }

  return (
    <>
      {/* Mobile: Card list with swipeable actions */}
      <Box hiddenFrom="md">
        <Stack gap="md">
          {recalls.map((recall) => {
            const effectiveId = getRecallEffectiveId(recall);
            return (
              <RecallCard
                key={effectiveId}
                recall={recall}
                onClick={onRecallClick}
                onDismiss={onDismissRecall}
                onResolve={onResolveRecall}
                onReopen={onReopenRecall}
                highlighted={effectiveId === highlightedRecallId}
                isUpdating={updatingRecallId === effectiveId}
                forceClose={shouldForceClose(effectiveId)}
                onForceCloseComplete={() =>
                  handleForceCloseComplete(effectiveId)
                }
                onSwipeStateChange={(isOpen) =>
                  handleSwipeStateChange(effectiveId, isOpen)
                }
              />
            );
          })}
          {renderLoadMore()}
        </Stack>
      </Box>

      {/* Desktop: Table */}
      <Box visibleFrom="md">
        <AppTable
          data={recalls}
          columns={columns}
          loading={loading}
          hasMore={hasMore}
          loadingMore={loadingMore}
          onLoadMore={onLoadMore}
          sortBy={sortBy}
          onSortChange={onSortChange}
          getRowKey={(record: VehicleRecall) => getRecallEffectiveId(record)}
          highlightRow={(record: VehicleRecall) => getRecallEffectiveId(record) === highlightedRecallId}
          selectable={false}
          emptyState={{
            title: t("vehicleRecalls:emptyState.title"),
            description: t("vehicleRecalls:emptyState.description"),
            iconName: "ShieldCheck",
            onClick: null,
          }}
          withBorder
          highlightOnHover
          size="sm"
        />
      </Box>
    </>
  );
};

export const VehicleRecallsList = React.memo(VehicleRecallsListComponent);